<template>
  <div class="chart-container bg-white rounded-lg shadow-md p-2">
    <div class="flex flex-col space-y-3 mb-2">
      <!-- First row: Title and controls -->
      <div class="flex justify-between items-center">
        <h3 class="text-lg font-semibold text-gray-900">{{ currencyPair.target }}/CZK</h3>
        <div class="flex items-center space-x-2">
          <!-- Refresh Button -->
          <button
            @click="refreshData"
            :disabled="store.isLoading(currencyPair.id)"
            class="p-1 text-gray-500 hover:text-blue-600 transition-colors disabled:opacity-50"
            title="Refresh data"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          <!-- Time Frame Buttons -->
          <div class="flex flex-wrap gap-1">
            <button
              v-for="timeframe in store.timeframes"
              :key="timeframe.value"
              @click="selectTimeframe(timeframe.value)"
              :class="[
                'px-2 sm:px-3 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap',
                selectedTimeframe === timeframe.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              ]"
            >
              {{ timeframe.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Second row: Current price and percentage labels -->
      <div v-if="currentPriceInfo" class="flex flex-wrap items-center gap-2">
        <span class="text-price-label font-bold text-gray-900">
          {{ formatPrice(currentPriceInfo.currentPrice) }}
        </span>
        <div class="flex flex-wrap items-center gap-1">
          <div
            v-for="period in periodDifferences"
            :key="period.label"
            :class="[
              'flex items-center text-price-label font-medium px-2 py-1 rounded whitespace-nowrap',
              period.percentage !== null && period.isPositive
                ? 'text-green-700 bg-green-100'
                : period.percentage !== null && !period.isPositive
                ? 'text-red-700 bg-red-100'
                : 'text-gray-600 bg-gray-100'
            ]"
          >
            <span class="font-semibold mr-1">{{ period.label }}</span>
            <span v-if="period.percentage !== null">
              {{ formatPercentage(period.percentage) }}
            </span>
            <span v-else>N/A</span>
          </div>
        </div>
      </div>
    </div>

    <div class="relative -mt-4 w-full">
      <!-- Loading State -->
      <div v-if="store.isLoading(currencyPair.id)" class="flex items-center justify-center h-64">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span class="ml-2 text-gray-600">Loading...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="store.getError(currencyPair.id)" class="flex items-center justify-center h-64">
        <div class="text-center max-w-sm mx-auto p-6 bg-red-50 rounded-lg border border-red-200">
          <div class="mb-3">
            <svg class="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 class="text-lg font-medium text-red-800 mb-2">Data Unavailable</h3>
          <p class="text-red-600 text-sm mb-4">{{ store.getError(currencyPair.id) }}</p>
          <div class="flex justify-center space-x-3">
            <button
              @click="loadData"
              :disabled="store.isLoading(currencyPair.id)"
              class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <span v-if="store.isLoading(currencyPair.id)" class="flex items-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="m100 50c0 5.523 4.477 10 10 10s10-4.477 10-10-4.477-10-10-10-10 4.477-10 10zm-9.4-7.071c-.781.781-.781 2.047 0 2.828l7.071 7.071c.781.781 2.047.781 2.828 0s.781-2.047 0-2.828l-7.071-7.071c-.781-.781-2.047-.781-2.828 0z"></path>
                </svg>
                Retrying...
              </span>
              <span v-else>Try Again</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Chart -->
      <div v-else class="h-64 w-full">
        <apexchart
          type="line"
          height="100%"
          width="100%"
          :options="chartOptions"
          :series="chartSeries"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useExchangeRateStore } from '@/stores/exchangeRateStore'
import type { CurrencyPair } from '@/types'

interface Props {
  currencyPair: CurrencyPair
}

const props = defineProps<Props>()
const store = useExchangeRateStore()
const selectedTimeframe = ref('6M')

// Get current price information
const currentPriceInfo = computed(() => {
  return store.getCurrentPrice(props.currencyPair.id)
})

// Calculate percentage differences for multiple periods using getPriceDiffLabels
const periodDifferences = computed(() => {
  const priceDiffs = store.getPriceDiffLabels(props.currencyPair.id)

  return [
    {
      label: '6M',
      percentage: priceDiffs.sixMonths?.diffPercent ?? null,
      isPositive: (priceDiffs.sixMonths?.diffPercent ?? 0) >= 0
    },
    {
      label: '1M',
      percentage: priceDiffs.oneMonth?.diffPercent ?? null,
      isPositive: (priceDiffs.oneMonth?.diffPercent ?? 0) >= 0
    },
    {
      label: '1W',
      percentage: priceDiffs.oneWeek?.diffPercent ?? null,
      isPositive: (priceDiffs.oneWeek?.diffPercent ?? 0) >= 0
    }
  ]
})

// Format price based on currency type
function formatPrice(price: number): string {
  if (props.currencyPair.type === 'crypto') {
    return price.toLocaleString('cs-CZ', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }) + ' CZK'
  }
  return price.toFixed(2) + ' CZK'
}

// Format percentage change
function formatPercentage(percent: number): string {
  const sign = percent >= 0 ? '+' : ''
  return sign + percent.toFixed(2) + '%'
}

const chartOptions = computed(() => {
  const percentiles = store.getPercentileValues(props.currencyPair.id)
  const data = store.getChartData(props.currencyPair.id, selectedTimeframe.value)

  // Assign zone values based on which percentile is lower/higher
  const greenZoneValue = Math.min(percentiles.oneMonth, percentiles.sixMonths)
  const yellowZoneValue = Math.max(percentiles.oneMonth, percentiles.sixMonths)

  // Calculate y-axis range based only on exchange rate data
  const dataValues = data.map(item => item.rate)
  const minRate = dataValues.length > 0 ? Math.min(...dataValues) : 0
  const maxRate = dataValues.length > 0 ? Math.max(...dataValues) : 0

  return {
    chart: {
      type: 'line',
      height: 250,
      zoom: {
        enabled: true
      },
      toolbar: {
        show: false
      }
    },
    stroke: {
      curve: ['smooth', 'smooth', 'smooth', 'smooth'],
      width: [0, 0, 0, 3]
    },
    colors: ['#D1FAE5', '#FEF3C7', '#FEE2E2', '#3B82F6'],
    fill: {
      type: ['solid', 'solid', 'solid', 'solid'],
      opacity: [0.5, 0.5, 0.5, 1]
    },
    xaxis: {
      type: 'datetime',
      labels: {
        format: selectedTimeframe.value === '1D' ? 'HH:mm' : 'dd MMM'
      }
    },
    yaxis: {
      title: {
        text: `Rate (${props.currencyPair.target}/CZK)`
      },
      labels: {
        formatter: (value: number) => {
          if (props.currencyPair.type === 'crypto') {
            return value.toLocaleString()
          }
          return value.toFixed(2)
        }
      },
      min: minRate > 0 ? minRate - (maxRate - minRate) / 20 : 0,
      max: maxRate > 0 ? maxRate + (maxRate - minRate) / 20 : undefined,
      forceNiceScale: true
    },
    tooltip: {
      x: {
        format: selectedTimeframe.value === '1D' ? 'HH:mm dd MMM' : 'dd MMM yyyy'
      },
      y: {
        formatter: (value: number) => {
          if (props.currencyPair.type === 'crypto') {
            return value.toLocaleString() + ' CZK'
          }
          return value.toFixed(2) + ' CZK'
        }
      }
    },
    grid: {
      borderColor: '#E5E7EB'
    },
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'center'
    }
  }
})

const chartSeries = computed(() => {
  const data = store.getChartData(props.currencyPair.id, selectedTimeframe.value)
  const percentiles = store.getPercentileValues(props.currencyPair.id)

  // Assign zone values based on which percentile is lower/higher
  const greenZoneValue = Math.min(percentiles.oneMonth, percentiles.sixMonths)
  const yellowZoneValue = Math.max(percentiles.oneMonth, percentiles.sixMonths)

  const series = []

  // Add background area series first (so they appear behind the main line)
  if (data.length > 0) {
    // Calculate boundaries
    const dataValues = data.map(item => item.rate)
    const minValue = Math.min(...dataValues)
    const maxValue = dataValues.length > 0 ? Math.max(...dataValues) : 0
    const upperBoundary = maxValue * 1.1 // 10% above maximum

    // Green zone (bottom stripe) - from 0 to lower percentile
    if (greenZoneValue > 0) {
      series.push({
        name: `Green Zone: ${props.currencyPair.type === 'crypto' ?
          greenZoneValue.toLocaleString() :
          greenZoneValue.toFixed(2)} ${props.currencyPair.target}/CZK`,
        type: 'rangeArea',
        data: data.map(item => ({
          x: new Date(item.date).getTime(),
          y: [0, greenZoneValue]
        }))
      })
    }

    // Yellow zone (middle stripe) - from lower to higher percentile
    if (greenZoneValue > 0 && yellowZoneValue > greenZoneValue) {
      series.push({
        name: `Yellow Zone: ${props.currencyPair.type === 'crypto' ?
          yellowZoneValue.toLocaleString() :
          yellowZoneValue.toFixed(2)} ${props.currencyPair.target}/CZK`,
        type: 'rangeArea',
        data: data.map(item => ({
          x: new Date(item.date).getTime(),
          y: [greenZoneValue, yellowZoneValue]
        }))
      })
    }

    // Red zone (top stripe) - from higher percentile to upper boundary
    if (yellowZoneValue > 0) {
      series.push({
        name: `Red Zone (above ${props.currencyPair.type === 'crypto' ?
          yellowZoneValue.toLocaleString() :
          yellowZoneValue.toFixed(2)})`,
        type: 'rangeArea',
        data: data.map(item => ({
          x: new Date(item.date).getTime(),
          y: [yellowZoneValue, upperBoundary]
        }))
      })
    }
  }

  // Add main exchange rate line last (so it appears on top)
  series.push({
    name: `${props.currencyPair.target}/CZK`,
    type: 'line',
    data: data.map(item => ({
      x: new Date(item.date).getTime(),
      y: item.rate
    }))
  })

  return series
})

function selectTimeframe(timeframe: string) {
  selectedTimeframe.value = timeframe
  loadData()
}

async function loadData() {
  // Clear any previous errors
  store.clearError(props.currencyPair.id)

  // Fetch data for the selected timeframe for the chart
  await store.fetchExchangeRateData(props.currencyPair.id, selectedTimeframe.value)
}

async function refreshData() {
  // Clear any previous errors
  store.clearError(props.currencyPair.id)

  // Refresh data for the selected timeframe
  await store.refreshData(props.currencyPair.id, selectedTimeframe.value)
}

onMounted(() => {
  loadData()
})

watch(() => props.currencyPair.id, () => {
  loadData()
})
</script>

<style scoped>
.chart-container {
  min-height: 350px;
}

.text-price-label {
  font-size: 0.6875rem; /* 11px, one point smaller than text-xs (12px) */
}
</style>
